/**
 * App-handoff redirect target. The "Open Habitify" CTA on the Success
 * page (and the paused-variation fallback in VariationApp) all funnel
 * through here so the user lands where they can actually install / open
 * the app for their device:
 *
 *   • iOS / iPadOS  → App Store listing
 *   • Android        → Google Play listing
 *   • Everything else (desktop, in-app browsers we can't sniff, etc.)
 *                     → web fallback (habitify.me)
 *
 * Override targets per platform via env vars — useful for staging,
 * regional store listings, or smart-link services (Branch, Adjust):
 *
 *   VITE_APP_STORE_URL    iOS deeplink / App Store URL
 *   VITE_PLAY_STORE_URL   Android deeplink / Play Store URL
 *   VITE_WEB_REDIRECT_URL Desktop fallback
 *
 * Detection rules (kept conservative — we'd rather send a desktop user
 * to web than send an iPhone user to the desktop site):
 *
 *  - iOS: classic `/iPad|iPhone|iPod/` UA test, PLUS the "iPad on
 *    iPadOS 13+" case where Safari masquerades as Mac and the only
 *    tell is `maxTouchPoints > 1`.
 *  - Android: substring match on UA.
 *  - Everything else (incl. MSStream which signals legacy IE/Edge
 *    Mobile reporting as iOS) → web.
 */

export type Platform = "ios" | "android" | "web";

const DEFAULTS: Record<Platform, string> = {
  ios: "https://apps.apple.com/us/app/habitify-habit-tracker/id1111447047",
  android: "https://play.google.com/store/apps/details?id=co.unstatic.habitify",
  web: "https://habitify.me",
};

/**
 * Detect the user's platform from `navigator`. Safe to call on the
 * server (returns "web") — but really only meaningful in the browser.
 */
export function detectPlatform(): Platform {
  if (typeof navigator === "undefined") return "web";

  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  // Legacy Windows Phone / IE Mobile pretended to be Safari; bail early.
  // (window.MSStream only exists in those old runtimes.)
  if (typeof window !== "undefined" && (window as { MSStream?: unknown }).MSStream) {
    return "web";
  }

  // iOS — covers iPhone/iPod, classic iPad, AND iPadOS 13+ where Safari
  // identifies as "Macintosh". The maxTouchPoints check is the
  // documented disambiguator (a Mac never reports >1).
  const isClassicIOS = /iPad|iPhone|iPod/.test(ua);
  const isIPadOS =
    platform === "MacIntel" &&
    typeof navigator.maxTouchPoints === "number" &&
    navigator.maxTouchPoints > 1;
  if (isClassicIOS || isIPadOS) return "ios";

  if (/Android/i.test(ua)) return "android";

  return "web";
}

/**
 * Return the URL the user should be sent to for "Open Habitify",
 * given their detected platform. Env-var overrides take precedence so
 * the marketing team can swap targets without a redeploy of code.
 */
export function getAppHandoffUrl(platform: Platform = detectPlatform()): string {
  if (platform === "ios") {
    return import.meta.env.VITE_APP_STORE_URL || DEFAULTS.ios;
  }
  if (platform === "android") {
    return import.meta.env.VITE_PLAY_STORE_URL || DEFAULTS.android;
  }
  return import.meta.env.VITE_WEB_REDIRECT_URL || DEFAULTS.web;
}

/**
 * Redirect the current tab to the right destination. Returns the
 * platform that was detected so callers can log/track it.
 */
export function openAppHandoff(): Platform {
  const platform = detectPlatform();
  if (typeof window !== "undefined") {
    window.location.href = getAppHandoffUrl(platform);
  }
  return platform;
}
